import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// oxlint-disable-next-line import/extensions
import { isPlanWorkItemTechChangesEnabled } from '@atlassian/agentic-planning/featureFlagProvider.tsx';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { mciKrAttributesFromSkillsAndPlugins } from '@atlassian/conversation-assistant-jsm-mci-kr/mci-kr-attributes-from-skills-and-plugins';
import type { AdditionalAnalyticAttributes } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type FetchStreamReturn,
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isSSEMessage,
	isStreamAnswerPart,
	isStreamEntityAnnotations,
	normalizeEntityAnnotationsMessage,
	isStreamAUPError,
	isStreamError,
	isStreamReconnectSupportPart,
	isStreamResponse,
	isStreamTaskStatus,
	isStreamTodoSnapshot,
	isStreamTrace,
	type ParsedSSEMessage,
	type RovoLabsConfirmedTools,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { MessageActionPayloadContext } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	endActiveAgentRun,
	notifyAgentRunStateForConversation,
} from '@atlassian/conversation-assistant-store/controllers/agent-mention-run-state';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	isStoreAssistant,
	StoreActionLoggableName,
	type StoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { RovoChatStreamTimeoutError } from '@atlassian/conversation-assistant-utils/RovoChatStreamTimeoutError';
import { withAsyncGeneratorTimeout } from '@atlassian/conversation-assistant-utils/withAsyncGeneratorTimeout';

import { completeAifcActionExperience } from '../../common/utils/aifc-experience-collect/complete-aifc-action-experience';
import { interactionMetrics } from '../../common/utils/browser-metrics/interactionMetrics';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import {
	buildToolsAttributeFromConfirmedTools,
	type ToolActionedAnalyticsAttribute,
} from '../analytics/tools-attributes';
import { redactToolKeyForPii } from '../analytics/tools-attributes/redactToolKeyForPii';

import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownData } from './addAnswerPartWithMarkdownData';
import { appendEntityAnnotationBatch } from './appendEntityAnnotationBatch';
import { addAssistantMessage } from './addAssistantMessage';
import { buildUnifiedStreamTaskTarget } from './buildUnifiedStreamTaskTarget';
import { clearFollowUpMessages } from './clearFollowUpMessages';
import { clearStatus } from './clearStatus';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createChatReconnectableStream } from './createChatReconnectableStream';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { createStreamMarkdownProcessor } from './createStreamMarkdownProcessor';
import { dedupeTraceMessagesWithLatest } from './dedupeTraceMessages';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { findStoreMessageIndex } from './findStoreMessageIndex';
import { generateTmpMessageId } from './generateTmpMessageId';
import { getInteractionModeExecutedFromStoreAssistant } from './getInteractionModeExecutedFromStoreAssistant';
import { getInteractionModeRequested } from './getInteractionModeRequested';
import { getInvokedFrom } from './getInvokedFrom';
import { getLastAgentMessageID } from './getLastAgentMessageID';
import { getTraceFromStreamMessage } from './getTraceFromStreamMessage';
import { handleStreamTaskStatus } from './handleStreamTaskStatus';
import { interactionModeRequestedAttributes } from './interactionModeRequestedAttributes';
import { isEmptyConversation } from './isEmptyConversation';
import { isFinalResponseAwaitingConfirmation } from './isFinalResponseAwaitingConfirmation';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { processAutoActionApprovalStreamMessage } from './processAutoActionApprovalStreamMessage';
import { populateConfirmedToolsContext } from './populateConfirmedToolsContext';
import { processAifcActionStreamMessage } from './process-aifc-action-stream-message/process-aifc-action-stream-message';
import { processInteractionConclusion } from './processInteractionConclusion';
import { processResearchStreamMessage } from './processResearchStreamMessage';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { replaceAnswerData } from './replaceAnswerData';
import { setConversationData } from './setConversationData';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, MessageSource, ResearchStreamState } from './types';
import { updateConfirmedToolsActionStatuses } from './updateConfirmedToolsActionStatuses';
import { updateStoreAssistantMessageId } from './updateStoreAssistantMessageId';

const ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS = 120_000;
const NO_FINAL_RESPONSE_ERROR_MESSAGE = 'The response took too long and timed out';

/**
 * Similar action to `sendMessage` with the barebone functionalities to confirm tools/skills/actions
 *
 * Initially this will assume that this function only created on existing conversation
 */
type SendMessageConfirmedToolsArgs = {
	conversationId?: string;
	agentId?: string;
	confirmed_tools: RovoLabsConfirmedTools;
	messageSources?: Partial<MessageSource>;
	storeUserMessages?: boolean;
	webSearchEnabled?: boolean;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	includeAIFeatureContext?: boolean;
	agentVersion?: AgentVersionInput;
	actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
	resolutionType?: 'confirmed' | 'cancelled';
	/**
	 * The following context-driving params are used (behind the
	 * `rovo-include-full-context-on-confirmed-tools` experiment) to build the same
	 * full request context that `sendMessage` sends, so the backend receives the
	 * creation/page/whiteboard/etc context on tool confirmation. All optional.
	 */
	productKey?: string;
	addCurrentPageAsContextUserPreference?: boolean;
	browserContextSanitizer?: BrowserContextSanitizer;
	/** Whether the staging area holds a created draft the user can refine. with disambiguation, its possible to be in the CWR staging area but have no content previewed yet. */
	hasStagedContent?: boolean;
};

export const sendMessageConfirmedTools: SafeAction<[SendMessageConfirmedToolsArgs]> =
	createSafeAction(
		StoreActionLoggableName.sendMessageConfirmedTools,
		({
			conversationId: conversationIdParam,
			agentId,
			confirmed_tools,
			messageSources,
			storeUserMessages = false,
			webSearchEnabled,
			deepResearchEnabled,
			thinkDeeperEnabled,
			alphaExtendedThinkingMode,
			fastModeEnabled,
			taskModeEnabled,
			includeAIFeatureContext,
			agentVersion,
			actionedAnalyticsAttributes,
			resolutionType,
			productKey,
			addCurrentPageAsContextUserPreference,
			browserContextSanitizer,
			hasStagedContent,
		}: SendMessageConfirmedToolsArgs): ChatStoreAction =>
			async (
				{ getState, dispatch },
				{
					assistanceService,
					chatContextStoreActions,
					experienceTracker,
					getMessages,
					setMessages,
					trackAIMAUEvent,
					trackAgentEvent,
					processStreamResearch,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					clearMessageResearch,
					updateResearchMessageId,
					markIncompleteResearchesErrored,
					sendAnalyticsEvent,
					getIsChatMounted,
					capabilities,
					unifiedTasksActions,
					processMessageStreamAction,
					processMessageStreamComplete,
					processMessageStreamActionClassification,
					onTempMessageIdReplaced,
					broadcastConfirmedActions,
				},
			) => {
				const conversationId = conversationIdParam ?? getState().currentConversationId;
				const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
				const resolvedAgentVersion =
					agentVersion ?? getState().conversations[conversationId]?.agentVersion;
				const confirmedToolsExperienceName = taskModeEnabled
					? ExperienceName.SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE
					: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE;

				// Collection of callbacks for experience and performance tracking
				const expAndPerfTrackers = {
					start: () => {
						experienceTracker.start({
							name: confirmedToolsExperienceName,
							id: conversationId,
							attributes: {
								conversationId,
								interactionSource: messageSources?.interactionSource,
							},
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.start();
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.start();
					},
					firstAnswerPartReceived: () => {
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
					},
					succeed: () => {
						experienceTracker.succeed({
							name: confirmedToolsExperienceName,
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					},
					aupError: () => {
						experienceTracker.succeed({
							name: confirmedToolsExperienceName,
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
					},
					agentDeactivated: ({ error }: { error: ApiError }) => {
						experienceTracker.fail({
							name: confirmedToolsExperienceName,
							error,
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
					},
					abort: () => {
						experienceTracker.abort({
							name: confirmedToolsExperienceName,
							reason: 'Aborted',
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
					},
					fail: ({ error }: { error: Error }) => {
						experienceTracker.fail({
							name: confirmedToolsExperienceName,
							error: error,
						});
						interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
						interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
					},
				};

				const requestedModeAttributes = interactionModeRequestedAttributes(
					getInteractionModeRequested({
						deepResearchEnabled,
						thinkDeeperEnabled,
						fastModeEnabled,
						taskModeEnabled,
					}),
				);

				const trackingAttributes: {
					requestId?: string;
					messageId?: string;
					humanMessageId?: string;
					instrumentationId?: string;
					initiatedTimestamp?: string;
					interactionModeExecuted?: string;
					isRovoCustomAgent: boolean | undefined;
					sendMessageReason: 'TOOLS_CONFIRMATION';
					scenarioId?: string;
					aiFeatureName?: string;
					invokedFrom?: string;
					agentExternalConfigReference?: string;
				} = {
					isRovoCustomAgent: isRovoCustomAgent(agentId),
					sendMessageReason: 'TOOLS_CONFIRMATION',
					aiFeatureName: messageSources?.aiFeatureName,
					invokedFrom: messageSources?.invokedFrom,
				};

				// Source executed mode from the originating assistant message (the one this
				// confirmed-tools action is being executed against). `confirmed_tools.message_id`
				// is the assistant message id whose tool calls the user just confirmed.
				const originatingAssistantMessage = getMessages(conversationId).find(
					(item): item is StoreAssistant =>
						isStoreAssistant(item) &&
						item.content?.message?.message?.id === confirmed_tools.message_id,
				);
				const interactionModeExecutedFromOriginatingAssistantMessage =
					getInteractionModeExecutedFromStoreAssistant(originatingAssistantMessage);

				const isCancelled = resolutionType === 'cancelled';

				const tools: ToolActionedAnalyticsAttribute[] =
					buildToolsAttributeFromConfirmedTools(confirmed_tools);

				const mciKrIdentifiers = tools
					.map((tool) => tool.toolKey)
					.filter((value): value is string => Boolean(value));

				const agentAnalyticsAttributes: {
					touchPoint: string;
					tools: ToolActionedAnalyticsAttribute[];
				} = {
					touchPoint: 'chatSendMessageConfirmedTools',
					tools,
				};

				// Collection of callbacks for analytics tracking
				const analyticsTrackers = {
					/**
					 * There are 2 AIMAU events here, because in resumption we're doing 2 things
					 * 1. We're confirming the previous tools result
					 * 2. And the confirmation triggers a LLM chat streaming
					 */
					initiated: ({ timestamp }: { timestamp?: string }) => {
						const actionKey = redactToolKeyForPii({
							toolDefinitionSource: confirmed_tools.tools[0]?.tool_source,
							key: confirmed_tools.tools[0]?.key,
						});
						trackAIMAUEvent({
							...trackingAttributes,
							type: 'actioned',
							actionResult: isCancelled ? 'toolsExecutionCancelled' : 'toolsExecutionConfirmed',
							proactive: false,
							singleInstrumentationID: trackingAttributes.instrumentationId,
							attributes: {
								...(isPlanWorkItemTechChangesEnabled() || fg('jira-ai-agents-improvements')
									? actionedAnalyticsAttributes
									: {}),
								...(interactionModeExecutedFromOriginatingAssistantMessage
									? {
											interactionModeExecuted:
												interactionModeExecutedFromOriginatingAssistantMessage,
										}
									: {}),
								...(actionKey ? { actionKey } : {}),
								...(trackingAttributes.aiFeatureName && {
									aiFeatureName: trackingAttributes.aiFeatureName,
								}),
							},
							// override the messageId to the confirmed_tools.message_id and not what's in trackingAttributes,
							// because action is on the original message not this message
							messageId: confirmed_tools.message_id,
						});

						trackAIMAUEvent({
							...trackingAttributes,
							type: 'initiated',
							subjectId: messageSources?.interactionSource,
							proactive: false,
							userGenerated: true,
							singleInstrumentationID: trackingAttributes.instrumentationId,
							timestamp,
							attributes: requestedModeAttributes,
						});

						// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
						if (agentId && isRovoCustomAgent(agentId)) {
							trackAgentEvent({
								action: isCancelled ? 'toolsExecutionCancelled' : 'toolsExecutionConfirmed',
								actionSubject: 'rovoAgent',
								attributes: {
									...trackingAttributes,
									...agentAnalyticsAttributes,
									agentId,
									singleInstrumentationId: trackingAttributes.instrumentationId,
									...((isPlanWorkItemTechChangesEnabled() || fg('jira-ai-agents-improvements')) &&
										actionedAnalyticsAttributes),
								},
							});
						}
					},
					aborted: () => {
						trackAIMAUEvent({
							...trackingAttributes,
							type: 'dismissed',
							subjectId: messageSources?.interactionSource,
							proactive: false,
							userGenerated: true,
							messageId: trackingAttributes.messageId,
							singleInstrumentationID: trackingAttributes.instrumentationId,
							attributes: requestedModeAttributes,
						});

						// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
						if (agentId && isRovoCustomAgent(agentId)) {
							trackAgentEvent({
								action: 'toolsExecutionStreamStopped',
								actionSubject: 'rovoAgent',
								attributes: {
									...trackingAttributes,
									...agentAnalyticsAttributes,
									agentId,
									singleInstrumentationId: trackingAttributes.instrumentationId,
								},
							});
						}
					},
					viewed: () => {
						trackAIMAUEvent({
							...trackingAttributes,
							type: 'viewed',
							proactive: false,
							userGenerated: true,
							source: messageSources?.interactionSource,
							messageId: trackingAttributes.messageId,
							subjectId: messageSources?.followUpSource,
							citedProductList: {},
							sourceProducts: {},
							singleInstrumentationID: trackingAttributes.instrumentationId,
							timestamp: new Date().toISOString(),
							attributes: {
								...(trackingAttributes.interactionModeExecuted
									? { interactionModeExecuted: trackingAttributes.interactionModeExecuted }
									: {}),
								...(trackingAttributes.aiFeatureName && {
									aiFeatureName: trackingAttributes.aiFeatureName,
								}),
								...(fg('add_mci_kr_attributes_to_rovo_chat_analytics')
									? mciKrAttributesFromSkillsAndPlugins(
											[],
											mciKrIdentifiers,
											trackingAttributes.agentExternalConfigReference,
										)
									: {}),
							},
						});

						// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
						if (agentId && isRovoCustomAgent(agentId)) {
							trackAgentEvent({
								action: 'toolsExecutionResultViewed',
								actionSubject: 'rovoAgent',
								attributes: {
									...trackingAttributes,
									...agentAnalyticsAttributes,
									agentId,
									singleInstrumentationId: trackingAttributes.instrumentationId,
									scenarioId: trackingAttributes.scenarioId,
								},
							});
						}
					},
					error: () => {
						trackAIMAUEvent({
							...trackingAttributes,
							type: 'error',
							proactive: false,
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							singleInstrumentationID: trackingAttributes.instrumentationId,
							timestamp: new Date().toISOString(),
							attributes: requestedModeAttributes,
						});

						// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
						if (agentId && isRovoCustomAgent(agentId)) {
							trackAgentEvent({
								action: 'toolsExecutionResultError',
								actionSubject: 'rovoAgent',
								attributes: {
									...trackingAttributes,
									...agentAnalyticsAttributes,
									agentId,
									singleInstrumentationId: trackingAttributes.instrumentationId,
								},
							});
						}
					},
				};

				const updateCurrentConversation = (data: Partial<Conversation>) => {
					dispatch(
						setConversationData({
							conversationId,
							data,
						}),
					);
				};

				const { abortController, storeAbortController, confirmAbort } =
					createStoreAbortController();

				let hasReceivedFinalResponse = false;
				let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
				let responseData: FetchStreamReturn<StreamMessage, boolean>['responseData'] | undefined;
				let hasLoggedInitiatedEvent = false;
				// Hoisted so it is accessible in the catch block for clearMessageResearch
				let tmpAssistantMessageId = generateTmpMessageId(conversationId);
				// Reconnect state — populated once we receive the conversation channel data message
				let agentMessageId: string | undefined;
				let isReconnectSupported = false;

				const handleAbort = () => {
					expAndPerfTrackers.abort();
					confirmAbort();
					clearMessageResearch({ messageId: tmpAssistantMessageId });
					!hasLoggedInitiatedEvent &&
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.aborted();

					// Call the cancel API to stop backend processing
					if (
						trackingAttributes.messageId &&
						trackingAttributes.messageId !== tmpAssistantMessageId
					) {
						assistanceService
							.cancelMessageStream({
								conversationId,
								messageId: trackingAttributes.messageId,
							})
							.catch(() => {
								// Log but don't throw - the abort is already happening
								addSentryBreadcrumb({
									message:
										'Failed to cancel message stream after abort in sendMessageConfirmedTools',
									data: {
										conversationId,
										messageId: trackingAttributes.messageId,
									},
								});
							});
					}
				};

				try {
					expAndPerfTrackers.start();

					// change the conversation status
					updateCurrentConversation({
						storeAbortController,
						status: CHAT_STORE_STATUS.awaitingResponse,
						lastAgentMessageStatus: 'IN_PROGRESS',
					});

					if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
						// Optimistic update goes directly to the tasks store. Explicitly clears
						// current_step / result_summary / error_message so the row resets to a
						// fresh "now running" state.
						const activeTask = unifiedTasksActions.getActiveTaskForConversation(conversationId);
						if (activeTask?.taskId) {
							unifiedTasksActions.setTaskProgressState({
								taskId: activeTask.taskId,
								progressState: 'Running',
								title: activeTask.title ?? '',
								updatedAt: new Date().toISOString(),
								currentStep: null,
								resultSummary: null,
								errorMessage: null,
							});
						}
					}

					// Only add assistant message
					dispatch(
						addAssistantMessage({
							id: tmpAssistantMessageId,
							content: '',
							conversationId,
							...(mentionAgentsEnabled ? { agentAuthorId: agentId } : { agentNamedId: agentId }),
							...(messageSources?.entryPoint ? { entryPoint: messageSources.entryPoint } : {}),
						}),
					);
					dispatch(clearFollowUpMessages({ conversationId }));
					trackingAttributes.messageId = tmpAssistantMessageId;
					trackingAttributes.instrumentationId = tmpAssistantMessageId;

					// Assumed the insert is successful and inserts at the last position
					const answerIndex = getMessages(conversationId).length - 1;

					// Behind the `rovo-include-full-context-on-confirmed-tools` experiment, build the
					// same full request context that `sendMessage` sends (creationContext +
					// page/whiteboard/slides/database/dashboard/browser/jira context) so the backend
					// routes tool confirmation through the same code paths as a regular send.
					// Derived to mirror a regular follow-up send: existing conversation
					// (`isFirstPrompt: false`) with content context resolved from the staging-area
					// signal. Failures degrade gracefully to the slim context below.
					let fullConfirmedToolsContext:
						| Partial<Awaited<ReturnType<typeof populateConfirmedToolsContext>>>
						| undefined;
					if (
						FeatureGates.getExperimentValue(
							'rovo-include-full-context-on-confirmed-tools',
							'isEnabled',
							false,
						)
					) {
						try {
							const isFirstPrompt = isEmptyConversation(conversationId);
							const contentContextValue = chatContextStoreActions.getIsStagingArea()
								? 'staging-area'
								: 'global';
							const isEmptyStagingArea =
								contentContextValue === 'staging-area' &&
								hasStagedContent === false &&
								expValEquals('cc-disambiguation-in-cwr', 'isEnabled', true);
							fullConfirmedToolsContext = await populateConfirmedToolsContext(
								chatContextStoreActions,
								isFirstPrompt,
								addCurrentPageAsContextUserPreference,
								contentContextValue,
								productKey,
								sendAnalyticsEvent,
								isEmptyStagingArea,
							);
						} catch (error) {
							addSentryBreadcrumb({
								message:
									'Failed to populate full context for sendMessageConfirmedTools; falling back to slim context',
								data: { conversationId },
							});
						}
					}

					// Call the BE endpoint

					trackingAttributes.initiatedTimestamp = new Date().toISOString();
					const response = await assistanceService.sendMessageConfirmedTools({
						conversationId,
						agentId,
						confirmed_tools,
						controller: abortController,
						optInSSEStream: true,
						storeMessage: storeUserMessages,
						webSearchEnabled,
						deepResearchEnabled,
						thinkDeeperEnabled,
						alphaExtendedThinkingMode,
						fastModeEnabled,
						...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
						...(includeAIFeatureContext && {
							aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
						}),
						...(fullConfirmedToolsContext
							? {
									editorContext: fullConfirmedToolsContext.editorContext,
									whiteboardContext: fullConfirmedToolsContext.whiteboardContext,
									slidesContext: fullConfirmedToolsContext.slidesContext,
									databaseContext: fullConfirmedToolsContext.databaseContext,
									dashboardEditorContext: fullConfirmedToolsContext.dashboardEditorContext,
									browserContext: fullConfirmedToolsContext.browserContext,
									additionalContext: fullConfirmedToolsContext.additionalContext,
									// aiFeatureContext is intentionally NOT spread here — it stays owned by the
									// `includeAIFeatureContext` guard above so callers that opt out are respected.
									jiraWorkflowContext: fullConfirmedToolsContext.jiraWorkflowContext,
									dashboardContext: fullConfirmedToolsContext.dashboardContext,
									useCurrentBrowsingContext: fullConfirmedToolsContext.useCurrentBrowsingContext,
									browserContextSanitizer,
								}
							: {}),
						...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
						optInToDeepResearchV2: true,
						optInThinkDeeperMessages: true,
						optInAnsNow: true,
						optInNewToolTraces: true,
						optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
						capabilities,
					});
					generator = response.generator;
					responseData = response.responseData;

					// Update action invocation statuses based on the confirmed_tools payload
					if (fg('rovo_chat_enable_tasks') || fg('fe_jira_admin_agent_eap')) {
						dispatch(
							updateConfirmedToolsActionStatuses({
								conversationId,
								messageId: confirmed_tools.message_id,
								tools: confirmed_tools.tools,
								status: 'fromResolution',
							}),
						);
					}

					/**
					 * --------
					 * Stream processing START
					 * --------
					 */

					updateCurrentConversation({
						status: CHAT_STORE_STATUS.typingResponse,
					});

					// Research store state shared across stream messages
					const researchState: ResearchStreamState = {
						clientRoundCounter: 0,
						hasAccumulatedAnswerContent: false,
					};

					const processStreamMarkdown = createStreamMarkdownProcessor();

					const isFullContextOnConfirmedToolsEnabled = FeatureGates.getExperimentValue(
						'rovo-include-full-context-on-confirmed-tools',
						'isEnabled',
						false,
					);

					// Tracks whether an AIFC action has started streaming. Mutated in place by
					// processAifcActionStreamMessage.
					const streamActionInfo = { isAction: false };
					const actionClassificationInvocationIds = new Set<string>();
					const actionCompleteInvocationIds = new Set<string>();
					let isBackendCreationRequest = false;

					const streamToIterate = createChatReconnectableStream({
						generator,
						conversationId,
						abortController,
						assistanceService,
						sendAnalyticsEvent,
						experienceTracker,
						getMessageId: () => agentMessageId,
						canReconnect: () => !!(agentMessageId && isReconnectSupported),
						updateCurrentConversation,
						getIsChatMounted,
						analyticsAttributes: {
							isDeepResearch: deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
						},
						onBeforeFirstReconnect: () => {
							if (agentMessageId) {
								const chat = getMessages(conversationId);
								const alreadySwapped = findStoreMessageIndex(chat, agentMessageId) >= 0;

								if (!alreadySwapped) {
									// Swap the tmp message ID → real agent message ID in the
									// message store so addAnswerPart/replaceAnswerData ID
									// validation doesn't throw on the replayed stream chunks.
									setMessages(
										conversationId,
										updateStoreAssistantMessageId({
											chat,
											oldId: tmpAssistantMessageId,
											newId: agentMessageId,
										}),
									);
									updateResearchMessageId({
										messageId: tmpAssistantMessageId,
										newMessageId: agentMessageId,
									});
								}

								// Reassign so subsequent processResearchStreamMessage calls
								// and addAnswerPart/replaceAnswerData use the real agent
								// message ID as the key.
								tmpAssistantMessageId = agentMessageId;
							}
						},
					});

					const isStreamTimeoutEnabled = FeatureGates.getExperimentValue(
						'rovo_chat_hung_stream_timeout',
						'isEnabled',
						false,
					);
					const streamIdleTimeoutMs = FeatureGates.getExperimentValue<number>(
						'rovo_chat_hung_stream_timeout',
						'timeoutMs',
						ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS,
					);

					const streamWithTimeout = isStreamTimeoutEnabled
						? withAsyncGeneratorTimeout(streamToIterate, {
								timeoutMs: streamIdleTimeoutMs,
								// Don't time out while offline – the reconnect layer waits for the
								// client to come back online, so timing out would lose the reconnect.
								isPaused: () => typeof navigator !== 'undefined' && !navigator.onLine,
								onTimeout: () => {
									if (hasReceivedFinalResponse || abortController.signal.aborted) {
										return undefined;
									}

									abortController.abort();
									const timeoutError = new RovoChatStreamTimeoutError(
										NO_FINAL_RESPONSE_ERROR_MESSAGE,
										{
											requestId: trackingAttributes.requestId,
											traceId: responseData?.traceId,
										},
									);
									sendAnalyticsEvent({
										eventType: 'track',
										action: 'timedOut',
										actionSubject: 'rovoChatStream',
										attributes: {
											conversationChannelId: conversationId,
											experienceName: confirmedToolsExperienceName,
											requestId: trackingAttributes.requestId,
											timeoutMs: streamIdleTimeoutMs,
											timeoutType: 'idle',
											traceId: responseData?.traceId,
										},
									});
									return timeoutError;
								},
							})
						: streamToIterate;

					for await (const streamObj of streamWithTimeout) {
						const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

						processInteractionConclusion({
							streamObj,
							streamMessage,
							conversationId,
							answerIndex,
							requestId: trackingAttributes.requestId,
							getMessages,
							setMessages,
							sendAnalyticsEvent,
						});

						/**
						 * Research store processing — handles think-deeper traces, implicit round
						 * detection, and research plan updates for all message types.
						 */
						processResearchStreamMessage({
							streamMessage,
							tmpAssistantMessageId,
							conversationId,
							deepResearchEnabled,
							getMessages,
							setMessages,
							processStreamResearch,
							addImplicitResearchPlan,
							resetHasReceivedAnswerPart,
							researchState,
						});

						/**
						 * Conversation channel data processing (usually the first stream part)
						 */
						if (isConversationChannelDataMessage(streamMessage)) {
							agentMessageId = streamMessage.assistant_message_id;
							trackingAttributes.instrumentationId = agentMessageId;
							trackingAttributes.messageId = agentMessageId;
							analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
							hasLoggedInitiatedEvent = true;
							// Update the research store with the real message ID once we have it
							updateResearchMessageId({
								messageId: tmpAssistantMessageId,
								newMessageId: agentMessageId,
							});
						}

						/**
						 * Reconnect support detection
						 */
						if (isStreamReconnectSupportPart(streamMessage)) {
							isReconnectSupported = true;
						}

						if (
							processAutoActionApprovalStreamMessage({
								streamMessage,
								conversationId,
								existingTraces: getState().conversations[conversationId]?.traces ?? [],
								updateTraces: (traces) => updateCurrentConversation({ traces }),
								sendAnalyticsEvent,
							})
						) {
							continue;
						}

						/**
						 * Trace processing
						 */
						if (isStreamTrace(streamMessage)) {
							if (!trackingAttributes.requestId) {
								trackingAttributes.requestId = streamMessage.metadata?.request_id;
							}

							updateCurrentConversation({
								traces: dedupeTraceMessagesWithLatest([
									...(getState().conversations[conversationId]?.traces ?? []),
									getTraceFromStreamMessage(streamMessage),
								]),
							});
						}

						/**
						 * AIFC action processing — streams generated content into the page via the
						 * editor bridge and emits AIFC experiences. Shared with the send-message
						 * flow. Gated behind the confirmed-tools full-context experiment.
						 */
						if (isFullContextOnConfirmedToolsEnabled) {
							const aifcActionResult = processAifcActionStreamMessage({
								streamMessage,
								conversationId,
								tmpAssistantMessageId,
								traceId: responseData?.traceId,
								productKey,
								deepResearchEnabled,
								storeUserMessages,
								streamActionInfo,
								trackingAttributes,
								experienceTracker,
								chatContextStoreActions,
								getMessages,
								setMessages,
								sendAnalyticsEvent,
								getIsChatMounted,
								processMessageStreamAction,
								processMessageStreamComplete,
								processMessageStreamActionClassification,
								promptStreamHook: undefined,
								openChat: undefined,
								messageSources,
								creationContextParams: undefined,
								agentId,
								agentName: undefined,
								agentIdentityAccountId: null,
								stopInteractionMetricsTimeToFirstActionPart: () => {},
							});
							if (aifcActionResult.actionClassificationInvocationId) {
								actionClassificationInvocationIds.add(
									aifcActionResult.actionClassificationInvocationId,
								);
							}
							if (aifcActionResult.actionCompleteInvocationId) {
								actionCompleteInvocationIds.add(aifcActionResult.actionCompleteInvocationId);
							}
							isBackendCreationRequest ||= aifcActionResult.isBackendCreationAction;
						}

						/**
						 * Answer part processing
						 */
						if (isStreamAnswerPart(streamMessage)) {
							expAndPerfTrackers.firstAnswerPartReceived();
							clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

							let processedStreamMessage = streamMessage;

							const processedStreamResult = processStreamMarkdown(processedStreamMessage);
							processedStreamMessage = processedStreamResult.processedStreamMessage;

							if (processedStreamResult.markdownData) {
								setMessages(
									conversationId,
									addAnswerPartWithMarkdownData({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
										markdownData: processedStreamResult.markdownData,
										useIndex: answerIndex,
									}),
								);
							} else {
								setMessages(
									conversationId,
									addAnswerPart({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
										useIndex: answerIndex,
									}),
								);
							}
						}

						if (
							isStreamEntityAnnotations(streamMessage) &&
							(fg('rovo_chat_inferred_project_hover_cards') ||
								fg('rovo_chat_people_entity_hover_cards'))
						) {
							setMessages(
								conversationId,
								appendEntityAnnotationBatch({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									entityAnnotationBatch: normalizeEntityAnnotationsMessage(streamMessage.message),
								}),
							);
						}

						/**
						 * Final response processing
						 */
						if (isStreamResponse(streamMessage)) {
							// A confirmed-tools turn either resolves (→ done) or ends returning an action
							// awaiting the user's confirmation (→ needs-input).
							notifyAgentRunStateForConversation(
								conversationId,
								isFinalResponseAwaitingConfirmation(streamMessage.message?.message?.actions)
									? 'needs-input'
									: 'done',
							);
							trackingAttributes.interactionModeExecuted =
								streamMessage.message?.message?.message_metadata?.interaction_mode_executed;
							hasReceivedFinalResponse = true;
							const shouldUseBackendCreationExperienceLifecycle =
								isBackendCreationRequest && isExperimentEnabled('cwr-be-based-multi-creation-v0');
							const hasActionClassificationOrCompletion =
								actionClassificationInvocationIds.size > 0 || actionCompleteInvocationIds.size > 0;

							if (
								shouldUseBackendCreationExperienceLifecycle &&
								hasActionClassificationOrCompletion
							) {
								// Backend creation responses do not require opening a preview to reach a terminal state.
								completeAifcActionExperience(
									experienceTracker,
									ExperienceName.AIFC_ALL_DATA_HANDLED,
									actionClassificationInvocationIds,
									actionCompleteInvocationIds,
									isBackendCreationRequest,
								);
							}
							// Always succeed AIFC_MESSAGE from FINAL_RESPONSE for backend multi-create events, or when
							// the parity experiment is enabled to streamline frontend and backend handling.
							// Ensure that isFullContextOnConfirmedToolsEnabled is enabled,
							// given this is otherwise only succeeded in processAifcActionStreamMessage
							if (
								isFullContextOnConfirmedToolsEnabled &&
								hasActionClassificationOrCompletion &&
								(shouldUseBackendCreationExperienceLifecycle ||
									isExperimentEnabled('cwr-fe-be-experience-analytics-streamline'))
							) {
								completeAifcActionExperience(
									experienceTracker,
									ExperienceName.AIFC_MESSAGE,
									actionClassificationInvocationIds,
									actionCompleteInvocationIds,
									isBackendCreationRequest,
								);
							}

							const humanMessageId = streamMessage?.message?.human_message?.id.toString();
							const finalAgentMessageId = streamMessage?.message?.message?.id.toString();

							clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

							const { processedStreamMessage: processedFinalMessage, markdownData } =
								processStreamResponseMarkdown({
									streamMessage,
									messageId: finalAgentMessageId,
									conversationId,
									sendAnalyticsEvent,
								});

							setMessages(
								conversationId,
								replaceAnswerData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									content: processedFinalMessage,
									useIndex: answerIndex,
									humanMessageId,
									markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
									onMessageIdChanged: isFullContextOnConfirmedToolsEnabled
										? onTempMessageIdReplaced
										: undefined,
								}),
							);

							// Update the research store key so rounds persist after the message ID changes
							if (finalAgentMessageId && finalAgentMessageId !== tmpAssistantMessageId) {
								updateResearchMessageId({
									messageId: tmpAssistantMessageId,
									newMessageId: finalAgentMessageId,
								});
							}

							trackingAttributes.humanMessageId = humanMessageId;
							const finalResponseAction = streamMessage.message.message?.actions?.[0];
							trackingAttributes.invokedFrom =
								getInvokedFrom({
									messageActionKey: finalResponseAction?.key,
									messageActionData: finalResponseAction?.data,
								}) ?? trackingAttributes.invokedFrom;
							trackingAttributes.scenarioId = streamMessage.message.message?.scenario?.id;
							trackingAttributes.agentExternalConfigReference = fg(
								'add_mci_kr_attributes_to_rovo_chat_analytics',
							)
								? streamMessage.message.message?.author?.external_config_reference
								: undefined;
							if (!hasLoggedInitiatedEvent) {
								trackingAttributes.messageId = finalAgentMessageId;
								trackingAttributes.instrumentationId = finalAgentMessageId;
							}
						}

						/**
						 * Follow up processing
						 */
						if (isFollowUpMessage(streamMessage)) {
							const messages = getMessages(conversationId);

							clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
							setMessages(conversationId, [
								...messages,
								{
									type: 'FOLLOW_UP',
									content: streamMessage,
									id: randomIntFromInterval(0, 1000000),
									agentMessageId: getLastAgentMessageID(messages),
									humanMessageId: trackingAttributes.humanMessageId,
								},
							]);
						}

						/**
						 * Task status processing
						 */
						if (isStreamTaskStatus(streamMessage)) {
							if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
								handleStreamTaskStatus({
									conversationId,
									taskMessage: streamMessage.message,
									messageId: trackingAttributes.messageId,
									target: buildUnifiedStreamTaskTarget(unifiedTasksActions),
								});
							}
						}

						/**
						 * Todo snapshot processing
						 */
						if (isStreamTodoSnapshot(streamMessage)) {
							if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
								unifiedTasksActions.setTaskTodoSnapshot({
									conversationId,
									todoSnapshot: { todos: streamMessage.message.todos },
								});
							}
						}

						/**
						 * Error processing
						 */
						if (isStreamError(streamMessage)) {
							const { message, metadata, millisOffset, type } = streamMessage;

							// AUP violations are still considered successful
							if (isStreamAUPError(streamMessage)) {
								expAndPerfTrackers.aupError();
							}

							throw new StreamErrorException(message?.content || 'Unknown', {
								detail: {
									message,
									metadata,
									millisOffset,
									type,
								},
								traceId: response.responseData?.traceId,
							});
						}
					}
				} catch (error) {
					const isAgentDeactivated =
						error instanceof ApiError &&
						error.errorAttributes.errorCode?.includes(
							ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED,
						);
					const isNoAgentPermission =
						error instanceof ApiError &&
						error.errorAttributes.errorCode?.includes(
							ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN,
						) &&
						fg('convo-ai-enable-agents-use-permission');

					if (isAgentDeactivated || isNoAgentPermission) {
						expAndPerfTrackers.agentDeactivated({ error });

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
							errorCode: fg('convo-ai-enable-agents-use-permission')
								? error.errorAttributes.errorCode
								: undefined,
						});

						// Mark all confirmed tool actions as errored
						if (fg('rovo_chat_enable_tasks')) {
							dispatch(
								updateConfirmedToolsActionStatuses({
									conversationId,
									messageId: confirmed_tools.message_id,
									tools: confirmed_tools.tools,
									status: 'errored',
								}),
							);
						}

						!hasLoggedInitiatedEvent &&
							analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
						analyticsTrackers.error();
						return;
					}

					if (error instanceof Error && error.name === 'AbortError') {
						handleAbort();
						return;
					}

					// Mark all confirmed tool actions as errored
					if (fg('rovo_chat_enable_tasks')) {
						dispatch(
							updateConfirmedToolsActionStatuses({
								conversationId,
								messageId: confirmed_tools.message_id,
								tools: confirmed_tools.tools,
								status: 'errored',
							}),
						);
					}

					expAndPerfTrackers.fail({
						error: error instanceof Error ? error : new Error(String(error)),
					});
					markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
					!hasLoggedInitiatedEvent &&
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.error();
					throw error;
				} finally {
					updateCurrentConversation(clearStatus({}));
					setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
					// Safety net for the agent-run bridge: clear a run left in flight by an abort or
					// dropped request so its node does not stay active. No-op once settled.
					endActiveAgentRun(conversationId);
				}

				/**
				 * --------
				 * Stream processing END
				 * --------
				 */

				// Handle programmatic abort — the generator may exit cleanly without throwing AbortError
				// when the abort controller is signalled. Check the signal to detect this case.
				if (abortController.signal.aborted) {
					handleAbort();
					return;
				}

				// if stream finishes without FINAL_RESPONSE, BE is hanging
				if (!hasReceivedFinalResponse) {
					const error = new NoFinalResponseError(
						'The response took too long and timed out. Try refreshing the page.',
						{
							traceId: responseData?.traceId,
						},
					);
					experienceTracker.fail({
						name: confirmedToolsExperienceName,
						error,
					});
					markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
					!hasLoggedInitiatedEvent &&
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.error();
					throw error;
				}

				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.viewed();
				expAndPerfTrackers.succeed();

				// Re-emit the confirmed-action broadcast that the legacy resolveConversationAction
				// path used to send, so content-bridge subscribers (e.g. Jira Plans) still refresh
				// after tools are confirmed via the resumption flow. See use-rovo-confirmed-actions.
				if (fg('rovo_chat_broadcast_confirmed_tools_actions')) {
					const confirmedActionContexts: MessageActionPayloadContext[] = confirmed_tools.tools
						.filter((tool) => tool.resolution?.type !== 'cancelled')
						.map((tool) => ({
							key: tool.key,
							invocationId: tool.invocation_id,
							messageId: confirmed_tools.message_id,
							data: tool.data.arguments,
							resolution: { type: 'confirm' },
						}));

					if (confirmedActionContexts.length > 0) {
						broadcastConfirmedActions?.(confirmed_tools.message_id, confirmedActionContexts);
					}
				}

				if (fg('rovo_chat_chat_history_refresh')) {
					//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
					if (getState().currentConversationId !== conversationId) {
						dispatch(
							setConversationData({
								conversationId,
								data: { readStatus: false },
							}),
						);
					}
				}
			},
	);
